import useAssetActions from "hook/use-asset-actions";
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AutoSizer, List } from 'react-virtualized';
import { useHistory } from 'react-router';
import useTrackItemSize from 'hook/use-track-item-size';
import { getTrackPageRoute } from 'utils/routes';
import { favoritesPropTypes, queuePropType } from 'utils/prop-types';
import TrackItem from 'components/track-list/track-item';

const ListResult = memo(
  (props) => {
    const {
      isPlaying,
      queue,
      activeIndex,
      activePlaylistId,
      pauseTrackAction,
      tracks,
      favorites,
      replaceQueueAction,
      addToFavoritesAction,
      removeFromFavoritesAction
    } = props;

    const itemStyleProps = useTrackItemSize({
      m: 0.271,
      l: 0.38,
      s: 0.35,
      xs: 0.301,
      xl: 0.36,
    });
    const history = useHistory();

    const listRef = useRef();

    const [visibleItemsIds, setVisibleItemsIds] = useState([...Array(tracks.length).keys()]);
    useEffect(() => {
      setVisibleItemsIds([...Array(tracks.length).keys()]);
    }, [tracks]);

    const { handlePlayClick, handlePauseClick, isTrackPlaying, isInFavorites, addToFavorites, removeFromFavorites } = useAssetActions({
      queue,
      items: tracks,
      favorites,
      isPlaying,
      activeIndex,
      visibleItemsIds,
      pauseTrackAction,
      activePlaylistId,
      replaceQueueAction,
      addToFavoritesAction,
      removeFromFavoritesAction,
      playlistId: 'search',
    });

    const handleInfoBtnClick = useCallback(
      (trackId) => (event) => {
        event.stopPropagation();
        history.push(getTrackPageRoute(trackId));
      },
      [],
    );

    const getRowHeight = useCallback(
      ({ index }) => (index === 0 ? itemStyleProps.calculatedHeight + 36 : itemStyleProps.calculatedHeight),
      [itemStyleProps.width],
    );

    useEffect(() => {
      if (listRef.current) {
        listRef.current.recomputeRowHeights();
      }
    }, [itemStyleProps.width]);

    return (
      <AutoSizer>
        {({ width, height }) => (
          <List
            ref={listRef}
            width={width}
            height={height}
            overscanRowCount={10}
            rowHeight={getRowHeight}
            overscanIndicesGetter={({ cellCount, overscanCellsCount, startIndex, stopIndex }) => ({
              overscanStartIndex: Math.max(0, startIndex - overscanCellsCount),
              overscanStopIndex: Math.min(cellCount - 1, stopIndex + overscanCellsCount),
            })}
            rowCount={tracks.length}
            rowRenderer={({ key, index, style }) => (
              <div key={key} style={{ ...style, ...(index === 0 ? { top: 36 } : undefined) }}>
                <TrackItem
                  trackIndex={index}
                  isInFavorites={isInFavorites}
                  onPlayClick={handlePlayClick}
                  itemStyleProps={itemStyleProps}
                  addToFavorites={addToFavorites}
                  onInfoIconClick={handleInfoBtnClick}
                  removeFromFavorites={removeFromFavorites}
                  onPauseClick={handlePauseClick}
                  isPlaying={isTrackPlaying(tracks?.[index]?.trackId, tracks?.[index]?.assetId, index)}
                  {...tracks[index]}
                />
              </div>
            )}
          />
        )}
      </AutoSizer>
    );
  },
);

ListResult.propTypes = {
  tracks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  ).isRequired,
  favorites: favoritesPropTypes,
  replaceQueueAction: PropTypes.func.isRequired,
  addToFavoritesAction: PropTypes.func.isRequired,
  removeFromFavoritesAction: PropTypes.func.isRequired,
  queue: queuePropType,
  activeIndex: PropTypes.number.isRequired,
  activePlaylistId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isPlaying: PropTypes.bool.isRequired,
  pauseTrackAction: PropTypes.func.isRequired,
};

export default ListResult;
